import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { User, LogOut, FileText, Briefcase, Video, Heart, Home, Bookmark, Send, TrendingUp } from "lucide-react";
import ResumeBuilder from "@/components/ResumeBuilder";
import FindJobs from "@/components/FindJobs";
import InterviewPrep from "@/components/InterviewPrep";
import SuccessStories from "@/components/SuccessStories";
import DashboardKPIs from "@/components/DashboardKPIs";
import ApplicationsTracker from "@/components/ApplicationsTracker";
import SavedJobs from "@/components/SavedJobs";
import RecentlyViewedJobs from "@/components/RecentlyViewedJobs";
import ProfileCompletion from "@/components/ProfileCompletion";
import NotificationsDropdown from "@/components/NotificationsDropdown";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// GDPR Article 25: Privacy by design - minimal data exposure on dashboard
// Security: Role-based UI with candidate-specific features
export default function CandidateDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("home");

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();
        
        setProfile(profileData);
      }
    };

    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          <h1 className="text-xl font-semibold">Second Chance Connect</h1>
          <div className="flex items-center gap-4">
            <NotificationsDropdown />
            <span className="text-sm text-muted-foreground">
              {profile?.email}
            </span>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome, {profile?.full_name || "Candidate"}</h2>
          <p className="text-muted-foreground">Your journey to new opportunities starts here.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="home" className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Applications</span>
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-2">
              <Bookmark className="h-4 w-4" />
              <span className="hidden sm:inline">Saved</span>
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">Browse</span>
            </TabsTrigger>
            <TabsTrigger value="resume" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Resume</span>
            </TabsTrigger>
            <TabsTrigger value="prep" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              <span className="hidden sm:inline">Prep</span>
            </TabsTrigger>
            <TabsTrigger value="stories" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              <span className="hidden sm:inline">Stories</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="space-y-6">
            {/* Dashboard KPIs */}
            <DashboardKPIs />

            {/* Quick Actions Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("applications")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-primary" />
                    My Applications
                  </CardTitle>
                  <CardDescription>Track your job applications</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full">
                    View Applications
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("saved")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bookmark className="h-5 w-5 text-primary" />
                    Saved Jobs
                  </CardTitle>
                  <CardDescription>Your bookmarked opportunities</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" variant="outline">
                    View Saved Jobs
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("jobs")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    Browse Jobs
                  </CardTitle>
                  <CardDescription>Find employers who believe in second chances</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" variant="outline">
                    Search Opportunities
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("resume")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Build Resume
                  </CardTitle>
                  <CardDescription>AI-powered resume builder</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" variant="outline">
                    Start Building
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("prep")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-primary" />
                    Interview Prep
                  </CardTitle>
                  <CardDescription>Practice with AI coaching</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" variant="outline">
                    Start Practicing
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab("stories")}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-primary" />
                    Success Stories
                  </CardTitle>
                  <CardDescription>Get inspired by real stories</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full" variant="outline">
                    Read Stories
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Recently Viewed & Profile Completion */}
            <div className="grid gap-6 md:grid-cols-2">
              <RecentlyViewedJobs />
              <ProfileCompletion />
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTracker />
          </TabsContent>

          <TabsContent value="saved">
            <SavedJobs />
          </TabsContent>

          <TabsContent value="resume">
            <ResumeBuilder />
          </TabsContent>

          <TabsContent value="jobs">
            <FindJobs />
          </TabsContent>

          <TabsContent value="prep">
            <InterviewPrep />
          </TabsContent>

          <TabsContent value="stories">
            <SuccessStories />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
